/**
 * DebugReport — user-initiated diagnostic bundle dialog.
 *
 * WHY full console + screenshot:
 * When Sentry isn't wired up (prod outages, or errors that don't throw),
 * the only way to diagnose is to replay what the user saw plus the
 * browser's log stream. Capture logic lives in ./debugReportCapture.
 *
 * WHY the download fallback (D-33 R2 — no silent failures):
 * The scout_activity_log insert requires a Supabase auth session, which
 * cookie-only SL users don't have. If the submit fails for ANY reason, the
 * user can still download the bundle locally and share it via Telegram /
 * a ticket. A report is never lost.
 */

import React, { useState, useCallback } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, Button, Badge,
} from '@appic/ui-components'
import { useCurrentUser } from '@/lib/auth/UserContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { Bug, Send, CheckCircle2, Loader2, AlertTriangle, Download, Lightbulb, CloudOff } from 'lucide-react'
import {
  collectDiagnostics,
  submitDiagnostics,
  downloadBundle,
  type DiagnosticData,
  type ReportKind,
} from './debugReportCapture'
import { DebugReportBugRefPanel } from './DebugReport.bugRefPanel'
import { DebugReportPreviewPanel } from './DebugReport.previewPanel'

// `queued` is the offline outcome: the submit failed transiently (or the user
// was offline) so the bundle was enqueued for automatic replay on reconnect —
// a distinct confirmation from `sent` (it hasn't reached the server yet) and
// from `error` (nothing was lost, no action needed).
type DialogState = 'consent' | 'collecting' | 'preview' | 'sending' | 'sent' | 'queued' | 'error'

interface DebugReportProps {
  open: boolean
  onClose: () => void
}

export function DebugReport({ open, onClose }: DebugReportProps) {
  const { t } = useLanguage()
  const { id, email } = useCurrentUser()
  const [state, setState] = useState<DialogState>('consent')
  // Report kind. Defaults to 'bug' so the dialog opens in its historical
  // mode; the user can switch to a feature request before collecting.
  const [kind, setKind] = useState<ReportKind>('bug')
  const [report, setReport] = useState<DiagnosticData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isFeature = kind === 'feature'
  // Kind-scoped i18n key — falls back to the shared `debug.report.*` key when
  // no feature-specific override exists (e.g. status copy that reads the same
  // for both modes).
  const tk = (suffix: string): string => {
    if (isFeature) {
      const feat = t(`debug.report.feature.${suffix}`)
      if (feat !== `debug.report.feature.${suffix}`) return feat
    }
    return t(`debug.report.${suffix}`)
  }
  // Free-text comment the user can attach before submitting. Kept in the
  // dialog-local state so it survives re-renders but is dropped on close.
  const [userComment, setUserComment] = useState('')

  // Normalise unknown errors — Supabase PostgrestError is a plain object,
  // not an Error instance, so `String(err)` used to render `[object Object]`
  // in the UI. Pull code + message + details + hint off any error-ish
  // object; else JSON.stringify; a non-serializable object (circular refs)
  // gets the explicit String(err) representation — a best-effort SERIALIZER
  // chain over an error we are already surfacing, not a swallowed failure.
  const messageOf = (err: unknown): string => {
    if (err instanceof Error) return err.message
    if (err && typeof err === 'object') {
      const o = err as Record<string, unknown>
      const parts = [o.code, o.message, o.details, o.hint]
        .filter((p): p is string => typeof p === 'string' && p.length > 0)
      if (parts.length > 0) return parts.join(' — ')
      try {
        return JSON.stringify(o)
      } catch {
        return String(err)
      }
    }
    return String(err)
  }

  const handleCollect = useCallback(async () => {
    setState('collecting')
    try {
      const data = await collectDiagnostics(email, id, kind)
      setReport(data)
      setState('preview')
    } catch (err) {
      setError(messageOf(err))
      setState('error')
    }
  }, [email, id, kind])

  // Attach the user's comment to the report just before submit/download —
  // keeps collectDiagnostics side-effect-free and lets the preview textarea
  // update without re-collecting.
  const withComment = useCallback(
    (r: DiagnosticData): DiagnosticData => ({ ...r, userComment: userComment.trim() || null }),
    [userComment],
  )

  const handleSend = useCallback(async () => {
    if (!report) return
    setState('sending')
    try {
      const outcome = await submitDiagnostics(withComment(report))
      // Offline / transient failure → the bundle was enqueued for replay on
      // reconnect. Show the queued confirmation, not a hard error.
      setState(outcome.status === 'queued' ? 'queued' : 'sent')
    } catch (err) {
      setError(messageOf(err))
      setState('error')
    }
  }, [report, withComment])

  const handleDownload = useCallback(() => {
    if (report) downloadBundle(withComment(report))
  }, [report, withComment])

  const handleClose = useCallback(() => {
    setState('consent')
    setKind('bug')
    setReport(null)
    setError(null)
    setUserComment('')
    onClose()
  }, [onClose])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isFeature ? <Lightbulb className="h-5 w-5" /> : <Bug className="h-5 w-5" />}
            {tk('title')}
          </DialogTitle>
          <DialogDescription>
            {state === 'consent' && tk('consentMessage')}
            {state === 'collecting' && tk('collecting')}
            {state === 'preview' && tk('previewMessage')}
            {state === 'sending' && tk('sending')}
            {state === 'sent' && tk('sentMessage')}
            {state === 'queued' && tk('queuedMessage')}
            {state === 'error' && tk('errorMessage')}
          </DialogDescription>
        </DialogHeader>

        {state === 'consent' && (
          <div
            className="flex gap-2"
            role="radiogroup"
            aria-label={t('debug.report.kindLabel')}
          >
            <Button
              type="button"
              variant={!isFeature ? 'default' : 'outline'}
              size="sm"
              role="radio"
              aria-checked={!isFeature}
              onClick={() => setKind('bug')}
              className="flex-1 gap-1.5"
              data-testid="report-kind-bug"
            >
              <Bug className="h-4 w-4" />
              {t('debug.report.kindBug')}
            </Button>
            <Button
              type="button"
              variant={isFeature ? 'default' : 'outline'}
              size="sm"
              role="radio"
              aria-checked={isFeature}
              onClick={() => setKind('feature')}
              className="flex-1 gap-1.5"
              data-testid="report-kind-feature"
            >
              <Lightbulb className="h-4 w-4" />
              {t('debug.report.kindFeature')}
            </Button>
          </div>
        )}

        {state === 'preview' && report && (
          <DebugReportPreviewPanel
            report={report}
            userComment={userComment}
            onUserCommentChange={setUserComment}
            t={t}
            tk={tk}
          />
        )}

        {state === 'sent' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle2 className="h-12 w-12 text-success" />
            <Badge variant="success">{tk('sent')}</Badge>
            {report?.bugRef && <DebugReportBugRefPanel bugRef={report.bugRef} kind={report.kind} />}
          </div>
        )}

        {state === 'queued' && (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="report-queued">
            <CloudOff className="h-12 w-12 text-warn" />
            <Badge variant="warn">{t('debug.report.queued')}</Badge>
            {report?.bugRef && <DebugReportBugRefPanel bugRef={report.bugRef} kind={report.kind} />}
            {report && (
              <Button onClick={handleDownload} variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                {t('debug.report.download')}
              </Button>
            )}
          </div>
        )}

        {state === 'error' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
            <p className="text-sm text-muted-foreground text-center">{error}</p>
            {report && (
              <Button onClick={handleDownload} variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                {t('debug.report.download')}
              </Button>
            )}
          </div>
        )}

        <DialogFooter>
          {state === 'consent' && (
            <>
              <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
              <Button onClick={handleCollect} className="gap-2">
                {isFeature ? <Lightbulb className="h-4 w-4" /> : <Bug className="h-4 w-4" />}
                {tk('collect')}
              </Button>
            </>
          )}
          {(state === 'collecting' || state === 'sending') && (
            <Button disabled className="gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {state === 'collecting' ? tk('collecting') : tk('sending')}
            </Button>
          )}
          {state === 'preview' && (
            <>
              <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
              <Button variant="outline" onClick={handleDownload} className="gap-2">
                <Download className="h-4 w-4" />
                {t('debug.report.download')}
              </Button>
              <Button onClick={handleSend} className="gap-2">
                <Send className="h-4 w-4" />
                {t('debug.report.send')}
              </Button>
            </>
          )}
          {(state === 'sent' || state === 'queued' || state === 'error') && (
            <Button onClick={handleClose}>{t('common.close')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
